// -----------------------------------------------------------------------------
// Libraries
// -----------------------------------------------------------------------------
import React, { useState } from "react";

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import { ScheduleLiveForm } from "components/Forms/Workout/ScheduleLiveForm";
import { WorkoutDetailsForm } from "components/Forms/Workout/WorkoutDetailsForm";
import { WorkoutMusicForm } from "components/Forms/Workout/WorkoutMusicForm";
import { ReleaseScheduleForm } from "components/Forms/Workout/ReleaseScheduleForm";
import { CloseIcon } from "components/Icons";
import { Steps } from "components/Common/Steps";
import { Paragraph12 } from "themes/default/_typography";

// -----------------------------------------------------------------------------
// Styles and utils
// -----------------------------------------------------------------------------
import StyledScheduleLiveModal from "./styles";
import UploadThumbnailForm from "components/Forms/Workout/UploadAssetsForm.jsx/UploadThumbnail";
import { Button, notification, Modal, Typography } from "antd";

import { useDispatch } from "react-redux";
import {
	useCreateWorkoutMutation,
	useGetGymWorkoutsQuery,
	useGetDomainGymQuery,
	useEditWorkoutMutation,
	useScheduleWorkoutMutation,
	useGetScheduledStreamsQuery,
	useDeleteWorkoutMutation,
	useGetCategoriesQuery,
} from "store/api/api";
import { updateSelectedWorkout } from "store/slices";
import { useParams } from "react-router";
import WorkoutPreview from "components/Forms/Workout/WorkoutPreview/Preview";
import { OfflineBanner } from "components/OfflineBanner";
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const ScheduleLiveModal = ({ open, setOpen }) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [values, setValues] = useState({});
	const dispatch = useDispatch();
	const [createWorkoutTrigger, createWorkoutResponse] = useCreateWorkoutMutation();
	const [editWorkoutTrigger, editWorkoutResponse] = useEditWorkoutMutation();
	const [scheduleLiveTrigger] = useScheduleWorkoutMutation();
	const [deleteWorkoutTrigger] = useDeleteWorkoutMutation();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const domainGymId = domainGymResponse.data?.id;
	const getWorkoutsResponse = useGetGymWorkoutsQuery({ gym_id: domainGymId }, { skip: !domainGymResponse.data });
	const scheduledStreams = useGetScheduledStreamsQuery(domainGymId, { skip: !domainGymResponse.data });
	const categoriesResponse = useGetCategoriesQuery(domainGymId, { skip: !domainGymResponse.data });

	const closeModal = async () => {
		setOpen(false);
		setCurrentStep(1);
		setConfirmOpen(false);
		// Delete created Draft workout
		if (values.workout_id && values.gym_id) {
			await deleteWorkoutTrigger({
				workout_id: values.workout_id,
				gym_id: values.gym_id,
			});
		}
		setValues({});
		getWorkoutsResponse.refetch();
	};

	const handleCloseModal = () => {
		setConfirmOpen(true);
	};

	const createOrEditWorkout = async (values) => {
		if (!values.workout_id) {
			const { data, error } = await createWorkoutTrigger(values);
			if (error) {
				return notification.error({
					message: "Something went wrong creating your workout",
				});
			}
			setValues((v) => ({ ...v, workout_id: data?.workout?.id }));
			dispatch(updateSelectedWorkout({ ...data }));
			return notification.success({
				message: "Draft workout created",
			});
		} else {
			setValues((v) => ({ ...v, ...values }));
			const { error } = await editWorkoutTrigger(values);
			if (error) {
				return notification.error({
					message: "Something went wrong updating your workout",
				});
			}
		}
	};

	const scheduleLiveWorkout = async (values) => {
		const { error, data } = await scheduleLiveTrigger(values);
		if (data) {
			notification.success({
				message: "Workout created successfully",
			});
		}
		if (error) {
			return notification.error({
				message: "Something went wrong updating your workout",
			});
		}
	};

	const nextStep = async (v) => {
		if (createWorkoutResponse.isLoading || editWorkoutResponse.isLoading) {
			return;
		}
		const newValues = { ...values, ...v };
		await createOrEditWorkout(newValues);
		if (currentStep <= 5) {
			setValues((v) => ({ ...v, ...newValues }));
			setCurrentStep(currentStep + 1);
		} else {
			await scheduleLiveWorkout(newValues);
			setTimeout(() => {
				setCurrentStep(1);
				setOpen(false);
				setValues({});
			}, 1000);
			scheduledStreams.refetch();
		}
		await categoriesResponse.refetch();
		await getWorkoutsResponse.refetch();
	};

	const previousStep = (vals) => {
		setValues((v) => ({ ...v, ...vals }));
		setCurrentStep((s) => s - 1);
	};

	return (
		<>
			<Modal
				title="Confirm"
				open={confirmOpen}
				onOk={() => closeModal()}
				onCancel={() => setConfirmOpen(false)}
				okText="Yes"
				cancelText="Go Back"
				maskClosable={false}
				destroyOnClose
				footer={
					<div className="sb-confirm-form__actions">
						<Button onClick={() => setConfirmOpen(false)} data-id="btnCancel">
							Go Back
						</Button>
						<Button onClick={closeModal} type="primary" data-id="btnConfirm">
							Confirm
						</Button>
					</div>
				}
				data-id="liveCancelConfirmModal"
			>
				Are you sure you want to cancel creating this workout?
			</Modal>
			<Modal
				closeIcon={<CloseIcon data-id="close-schedule-live-modal" />}
				open={open}
				onCancel={handleCloseModal}
				footer={null}
				maskClosable={false}
				destroyOnClose
				width={700}
				bodyStyle={{ touchAction: "auto" }} // allow iOS scroll
				style={{ overflow: "visible" }}
			>
				<StyledScheduleLiveModal data-id="schedule-live-modal" className="sb-schedule-live-modal">
					<OfflineBanner
						text="You are Currently Offline. Your changes may not be saved"
						style={{ padding: 8, borderRadius: 4 }}
					/>
					<div className="sb-schedule-live-modal__header">
						<Steps
							className="sb-schedule-live-modal__header__steps"
							steps={[
								{ value: "", active: currentStep === 1 },
								{ value: "", active: currentStep === 2 },
								{ value: "", active: currentStep === 3 },
								{ value: "", active: currentStep === 4 },
								{ value: "", active: currentStep === 5 },
								{ value: "", active: currentStep === 6 },
							]}
						/>
						{currentStep === 1 && (
							<>
								<Typography.Title level={3}>Schedule Live Workout</Typography.Title>
								<Paragraph12>Fill out the information below to schedule a Live Workout</Paragraph12>
							</>
						)}
						{currentStep === 2 && (
							<>
								<Typography.Title level={3}>Upload Image</Typography.Title>
							</>
						)}
						{currentStep === 3 && (
							<>
								<Typography.Title level={3}>Fill in the Details</Typography.Title>
								<Paragraph12>Add some extra details to help us organize your workout.</Paragraph12>
							</>
						)}
						{currentStep === 4 && (
							<>
								<Typography.Title level={3}>Select Playlist</Typography.Title>
								<Paragraph12>Spice up your video with our unique music mixes.</Paragraph12>
							</>
						)}
						{currentStep === 5 && (
							<>
								<Typography.Title level={3}>Finalize Release Schedule</Typography.Title>
								<Paragraph12>Select date and time for your live class</Paragraph12>
							</>
						)}
						{currentStep === 6 && (
							<>
								<Typography.Title level={3}>Preview</Typography.Title>
							</>
						)}
					</div>
					{currentStep === 1 && (
						<ScheduleLiveForm
							values={values}
							nextStep={nextStep}
							loading={createWorkoutResponse.isLoading || editWorkoutResponse.isLoading}
							preRecordedLive
						/>
					)}
					{currentStep === 2 && (
						<UploadThumbnailForm
							previousStep={previousStep}
							nextStep={nextStep}
							values={values}
							setValues={setValues}
							loading={createWorkoutResponse.isLoading || editWorkoutResponse.isLoading}
						/>
					)}
					{currentStep === 3 && (
						<WorkoutDetailsForm
							nextStep={nextStep}
							live
							previousStep={previousStep}
							values={values}
							loading={createWorkoutResponse.isLoading || editWorkoutResponse.isLoading}
						/>
					)}
					{currentStep === 4 && (
						<WorkoutMusicForm
							nextStep={nextStep}
							live
							previousStep={previousStep}
							values={values}
							loading={createWorkoutResponse.isLoading || editWorkoutResponse.isLoading}
							maxWidth={442}
						/>
					)}
					{currentStep === 5 && (
						<ReleaseScheduleForm
							nextStep={nextStep}
							previousStep={previousStep}
							values={values}
							loading={createWorkoutResponse.isLoading || editWorkoutResponse.isLoading}
						/>
					)}
					{currentStep === 6 && (
						<WorkoutPreview
							loading={createWorkoutResponse.isLoading || editWorkoutResponse.isLoading}
							previousStep={() => setCurrentStep((s) => s - 1)}
							values={values}
							nextStep={nextStep}
						/>
					)}
				</StyledScheduleLiveModal>
			</Modal>
		</>
	);
};

export default ScheduleLiveModal;

